import { animate, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const DURATION = 0.7;
const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * A figure that rolls to its value rather than snapping to it.
 *
 * It rolls from wherever it currently sits, not from zero, so the first paint counts up from nothing
 * and a later change — collecting points, say — reads as this balance moving rather than a different
 * number replacing it. An interrupted roll carries on from the digit already on screen.
 *
 * Reduced motion gets the figure itself, immediately.
 */
export function useCountUp(value: number) {
	const prefersReducedMotion = useReducedMotion();
	const [shown, setShown] = useState(0);
	const position = useRef(0);

	useEffect(() => {
		if (prefersReducedMotion) {
			position.current = value;
			setShown(value);
			return;
		}

		const controls = animate(position.current, value, {
			duration: DURATION,
			ease: EASE,
			onUpdate: (current) => {
				position.current = current;
				setShown(Math.round(current));
			},
		});

		return () => controls.stop();
	}, [value, prefersReducedMotion]);

	return shown;
}
